import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Bell, Mail, Smartphone, AlertTriangle, Info, CheckCircle, ToggleLeft, ToggleRight } from 'lucide-react';

const NotificationSettings: React.FC = () => {
  const [emailNotifications, setEmailNotifications] = useState({
    energyAlerts: true,
    systemUpdates: true,
    maintenanceReminders: true,
    weeklyReports: true,
    securityAlerts: true,
    billingUpdates: false,
  });

  const [pushNotifications, setPushNotifications] = useState({
    criticalAlerts: true,
    dailySummary: false,
    systemStatus: true,
    newFeatures: false,
  });

  const [preferences, setPreferences] = useState({
    frequency: 'instant',
    quietHours: true,
    quietStart: '22:00',
    quietEnd: '07:00',
  });

  const toggleEmail = (key: keyof typeof emailNotifications) => {
    setEmailNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePush = (key: keyof typeof pushNotifications) => {
    setPushNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const Toggle: React.FC<{ enabled: boolean; onToggle: () => void }> = ({ enabled, onToggle }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onToggle}
      className={`flex items-center rounded-full p-1 transition-all duration-200 ${
        enabled ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      {enabled ? <ToggleRight className="h-6 w-6 text-white" /> : <ToggleLeft className="h-6 w-6 text-gray-600" />}
    </motion.button>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 p-3">
          <Bell className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600">Manage how and when you receive notifications</p>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Email Notifications</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-red-100 p-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Energy Alerts</h4>
                <p className="text-sm text-gray-600">Critical energy system notifications</p>
              </div>
            </div>
            <Toggle enabled={emailNotifications.energyAlerts} onToggle={() => toggleEmail('energyAlerts')} />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-100 p-2">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">System Updates</h4>
                <p className="text-sm text-gray-600">Software updates and new features</p>
              </div>
            </div>
            <Toggle enabled={emailNotifications.systemUpdates} onToggle={() => toggleEmail('systemUpdates')} />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-yellow-100 p-2">
                <Bell className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Maintenance Reminders</h4>
                <p className="text-sm text-gray-600">Scheduled maintenance notifications</p>
              </div>
            </div>
            <Toggle
              enabled={emailNotifications.maintenanceReminders}
              onToggle={() => toggleEmail('maintenanceReminders')}
            />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-100 p-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Weekly Reports</h4>
                <p className="text-sm text-gray-600">Energy consumption summaries</p>
              </div>
            </div>
            <Toggle enabled={emailNotifications.weeklyReports} onToggle={() => toggleEmail('weeklyReports')} />
          </div>
        </div>
      </div>

      {/* Push Notifications */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Smartphone className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Push Notifications</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-red-100 p-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Critical Alerts</h4>
                <p className="text-sm text-gray-600">Immediate attention required</p>
              </div>
            </div>
            <Toggle enabled={pushNotifications.criticalAlerts} onToggle={() => togglePush('criticalAlerts')} />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-100 p-2">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Daily Summary</h4>
                <p className="text-sm text-gray-600">End of day energy report</p>
              </div>
            </div>
            <Toggle enabled={pushNotifications.dailySummary} onToggle={() => togglePush('dailySummary')} />
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Preferences</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Notification Frequency</label>
            <select
              value={preferences.frequency}
              onChange={(e) => setPreferences({ ...preferences, frequency: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
            >
              <option value="instant">Instant</option>
              <option value="hourly">Hourly Digest</option>
              <option value="daily">Daily Digest</option>
              <option value="weekly">Weekly Digest</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Language</label>
            <select className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none">
              <option value="el">Ελληνικά</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">Quiet Hours</h4>
              <p className="text-sm text-gray-600">Pause non-critical notifications during these hours</p>
            </div>
            <Toggle
              enabled={preferences.quietHours}
              onToggle={() => setPreferences({ ...preferences, quietHours: !preferences.quietHours })}
            />
          </div>

          {preferences.quietHours && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Start Time</label>
                <input
                  type="time"
                  value={preferences.quietStart}
                  onChange={(e) => setPreferences({ ...preferences, quietStart: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="time"
                  value={preferences.quietEnd}
                  onChange={(e) => setPreferences({ ...preferences, quietEnd: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
                />
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6">
        <button className="rounded-xl bg-gray-200 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-300">
          Reset to Defaults
        </button>
        <button className="rounded-xl bg-blue-600 px-6 py-3 font-medium text-white transition-all hover:bg-blue-700">
          Save Preferences
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
